// Mock data for QSC America website

export const isoServices = [
  {
    id: 'iso-9001',
    code: 'ISO 9001:2015',
    title: 'Quality Management Systems',
    shortDescription: 'Enhance customer satisfaction and operational efficiency with internationally recognized quality management standards.',
    fullDescription: 'ISO 9001:2015 is the international standard for Quality Management Systems (QMS). It provides a framework for organizations to ensure they consistently meet customer requirements and enhance customer satisfaction through effective processes and continual improvement.',
    benefits: [
      'Improved operational efficiency and productivity',
      'Enhanced customer satisfaction and loyalty',
      'Better decision-making through data-driven insights',
      'Increased market credibility and competitive advantage',
      'Systematic approach to risk management',
      'Foundation for continual improvement'
    ],
    whoNeedsIt: 'Organizations of any size and sector looking to demonstrate consistent quality in their products and services.',
    icon: 'CheckCircle',
    color: '#003f6d'
  },
  {
    id: 'iso-20000-1',
    code: 'ISO/IEC 20000-1:2018',
    title: 'IT Service Management',
    shortDescription: 'Optimize IT service delivery and align technology with business objectives through proven service management practices.',
    fullDescription: 'ISO/IEC 20000-1:2018 is the international standard for IT Service Management Systems (SMS). It enables organizations to deliver managed IT services that meet business and customer needs, aligning with ITIL best practices.',
    benefits: [
      'Improved IT service quality and reliability',
      'Better alignment of IT services with business needs',
      'Enhanced customer satisfaction with IT services',
      'Reduced IT service costs and risks',
      'Improved IT service continuity and availability',
      'Competitive advantage in IT service delivery'
    ],
    whoNeedsIt: 'IT service providers, internal IT departments, and organizations dependent on effective IT service delivery.',
    icon: 'Server',
    color: '#c1272d'
  },
  {
    id: 'iso-27001',
    code: 'ISO/IEC 27001:2022',
    title: 'Information Security Management',
    shortDescription: 'Protect your valuable information assets and demonstrate commitment to data security with comprehensive security management.',
    fullDescription: 'ISO/IEC 27001:2022 is the international standard for Information Security Management Systems (ISMS). It provides a systematic approach to managing sensitive company information, ensuring confidentiality, integrity, and availability.',
    benefits: [
      'Systematic approach to information security risk management',
      'Protection against data breaches and cyber threats',
      'Enhanced trust with customers and stakeholders',
      'Compliance with legal and regulatory requirements',
      'Reduced security incidents and associated costs',
      'Competitive advantage in security-conscious markets'
    ],
    whoNeedsIt: 'Organizations handling sensitive data, including financial services, healthcare, technology, and government sectors.',
    icon: 'Shield',
    color: '#d4af37'
  },
  {
    id: 'iso-42001',
    code: 'ISO/IEC 42001:2023',
    title: 'AI Management Systems',
    shortDescription: 'Lead in responsible AI governance with the world\'s first international standard for artificial intelligence management.',
    fullDescription: 'ISO/IEC 42001:2023 is the groundbreaking international standard for Artificial Intelligence Management Systems (AIMS). It helps organizations responsibly develop, deploy, and use AI systems throughout their lifecycle, addressing AI-specific risks and ethics.',
    benefits: [
      'Responsible and ethical AI governance',
      'Systematic management of AI-specific risks',
      'Enhanced transparency and accountability in AI systems',
      'Competitive advantage in AI adoption',
      'Trust building with stakeholders and customers',
      'Alignment with emerging AI regulations'
    ],
    whoNeedsIt: 'Organizations developing or deploying AI systems, including technology companies, enterprises adopting AI, and AI service providers.',
    icon: 'Brain',
    color: '#003f6d'
  }
];

export const certificationSteps = [
  {
    step: 1,
    title: 'Initial Contact',
    description: 'Reach out to us for a no-obligation certification briefing. We\'ll understand your needs and explain the certification process.',
    icon: 'MessageSquare'
  },
  {
    step: 2,
    title: 'Application & Readiness Review',
    description: 'We review your application, confirm the certification scope, and assess readiness for Stage 1. No consultancy is provided — this is a scoping step to ensure the audit is planned correctly.',
    icon: 'Search'
  },
  {
    step: 3,
    title: 'Documentation Review',
    description: 'We review your management system documentation to ensure it meets the standard\'s requirements before the on-site audit.',
    icon: 'FileText'
  },
  {
    step: 4,
    title: 'Stage 1 Audit',
    description: 'An initial audit to verify your readiness for full certification and identify any areas requiring attention.',
    icon: 'ClipboardCheck'
  },
  {
    step: 5,
    title: 'Stage 2 Audit',
    description: 'Comprehensive on-site audit to verify effective implementation of your management system across the organization.',
    icon: 'Building'
  },
  {
    step: 6,
    title: 'Certification',
    description: 'Upon successful completion, you receive your ISO certification and can proudly display your achievement.',
    icon: 'Award'
  }
];


export const industries = [
  {
    name: 'Technology & IT',
    description: 'Software companies, IT service providers, and technology enterprises requiring ISO/IEC 20000-1:2018, ISO/IEC 27001:2022, and ISO/IEC 42001:2023.',
    icon: 'Laptop',
    standards: ['ISO/IEC 20000-1:2018', 'ISO/IEC 27001:2022', 'ISO/IEC 42001:2023']
  },
  {
    name: 'Healthcare',
    description: 'Hospitals, clinics, and healthcare providers ensuring quality and information security with ISO 9001:2015 and 27001.',
    icon: 'Heart',
    standards: ['ISO 9001:2015', 'ISO/IEC 27001:2022']
  },
  {
    name: 'Financial Services',
    description: 'Banks, insurance companies, and financial institutions requiring robust information security and quality management.',
    icon: 'DollarSign',
    standards: ['ISO 9001:2015', 'ISO/IEC 27001:2022']
  },
  {
    name: 'Manufacturing',
    description: 'Manufacturing organizations seeking to enhance operational efficiency and product quality through ISO 9001:2015.',
    icon: 'Factory',
    standards: ['ISO 9001:2015']
  },
  {
    name: 'Government & Public Sector',
    description: 'Government agencies and public organizations ensuring quality service delivery and information security.',
    icon: 'Landmark',
    standards: ['ISO 9001:2015', 'ISO/IEC 27001:2022']
  },
  {
    name: 'Professional Services',
    description: 'Legal practices and service organizations demonstrating quality and information security excellence.',
    icon: 'Briefcase',
    standards: ['ISO 9001:2015', 'ISO/IEC 27001:2022']
  }
];

export const trainingPrograms = [
  {
    title: 'ISO 9001:2015 Foundation Training',
    duration: '2 Days',
    level: 'Foundation',
    description: 'Introduction to Quality Management Systems and ISO 9001:2015 requirements.',
    standard: 'ISO 9001:2015'
  },
  {
    title: 'ISO 9001:2015 Internal Auditor',
    duration: '3 Days',
    level: 'Intermediate',
    description: 'Learn to conduct internal audits of Quality Management Systems.',
    standard: 'ISO 9001:2015'
  },
  {
    title: 'ISO/IEC 20000-1:2018 Foundation',
    duration: '2 Days',
    level: 'Foundation',
    description: 'Understanding IT Service Management principles and ISO/IEC 20000-1:2018 standard.',
    standard: 'ISO/IEC 20000-1:2018'
  },
  {
    title: 'ISO/IEC 27001:2022 Lead Implementer',
    duration: '5 Days',
    level: 'Advanced',
    description: 'Master the implementation of Information Security Management Systems.',
    standard: 'ISO/IEC 27001:2022'
  },
  {
    title: 'ISO/IEC 27001:2022 Internal Auditor',
    duration: '3 Days',
    level: 'Intermediate',
    description: 'Learn to audit Information Security Management Systems effectively.',
    standard: 'ISO/IEC 27001:2022'
  },
  {
    title: 'ISO/IEC 42001:2023 Foundation',
    duration: '2 Days',
    level: 'Foundation',
    description: 'Introduction to AI Management Systems and responsible AI governance.',
    standard: 'ISO/IEC 42001:2023'
  }
];

export const faqs = [
  {
    question: 'How long does the certification process take?',
    answer: 'The certification timeline varies depending on the size and complexity of your organization, typically ranging from 3 to 6 months from initial contact to certification.'
  },
  {
    question: 'What is the cost of ISO certification?',
    answer: 'Certification costs depend on factors including organization size, scope of certification, and complexity. Contact us for a customized quote based on your specific needs.'
  },
  {
    question: 'How long is the certification valid?',
    answer: 'ISO certifications are valid for three years, with annual surveillance audits to ensure continued compliance with the standard.'
  },
  {
    question: 'Do you offer remote auditing?',
    answer: 'Yes, we offer flexible auditing options including remote audits, hybrid approaches, and traditional on-site audits to meet your organizational needs.'
  },
  {
    question: 'Can we get certified for multiple ISO standards?',
    answer: 'Absolutely! Many organizations pursue integrated management systems covering multiple standards. We can conduct combined audits for efficiency.'
  },
  {
    question: 'What support do you provide after certification?',
    answer: 'We provide ongoing support including annual surveillance audits, recertification audits, and training programs to help maintain your certification.'
  }
];

export const testimonials = [
  {
    name: 'Sarah Johnson',
    position: 'CTO',
    company: 'TechVision Solutions',
    content: 'QSC America guided us through ISO/IEC 27001:2022 certification with exceptional professionalism. Their expertise gave us confidence in our information security practices.',
    rating: 5,
    standard: 'ISO/IEC 27001:2022'
  },
  {
    name: 'Michael Chen',
    position: 'Quality Director',
    company: 'Precision Manufacturing Inc.',
    content: 'The ISO 9001:2015 certification process was smooth and educational. QSC America\'s auditors were knowledgeable and provided valuable insights for improvement.',
    rating: 5,
    standard: 'ISO 9001:2015'
  },
  {
    name: 'Emily Rodriguez',
    position: 'IT Service Manager',
    company: 'Global IT Services',
    content: 'Achieving ISO/IEC 20000-1:2018 certification transformed our IT service delivery. QSC America\'s support throughout the process was outstanding.',
    rating: 5,
    standard: 'ISO/IEC 20000-1:2018'
  },
  {
    name: 'David Park',
    position: 'AI Ethics Lead',
    company: 'AI Innovations Corp',
    content: 'Being among the first to achieve ISO/IEC 42001:2023 certification positioned us as leaders in responsible AI. QSC America\'s expertise in this emerging standard is unmatched.',
    rating: 5,
    standard: 'ISO/IEC 42001:2023'
  }
];
